// Cross-subdomain SSO via a parent-domain cookie. The cookie is the
// source of truth — any subdomain on `.tinytinytelg2.space` can read it
// (it is intentionally NOT HttpOnly so the SPA can sync into localStorage
// as a fallback). Pasting the token on any one subdomain makes it work
// on portal, basbot, and tcr without re-pasting.

const STORAGE_KEY = 'basbot_token';
const EXPIRY_KEY = 'basbot_token_expiry';
const COOKIE_NAME = 'basbot_token';
// Derived from the current host so the SSO cookie works on whatever apex
// canonical domain the portal is served from.
const PARENT_DOMAIN = typeof location !== 'undefined' ? location.hostname.split('.').slice(-2).join('.') : 'tinytinytelg2.space';
const TTL_MS = 90 * 86_400_000;
const TTL_SECONDS = 90 * 86_400;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(/;\s*/);
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq) === name) return decodeURIComponent(part.slice(eq + 1));
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${name}=${encodeURIComponent(value)}; path=/; ` +
    `domain=.${PARENT_DOMAIN}; max-age=${TTL_SECONDS}; secure; samesite=lax`;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${name}=; path=/; domain=.${PARENT_DOMAIN}; max-age=0; secure; samesite=lax`;
}

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  const fromCookie = readCookie(COOKIE_NAME);
  if (fromCookie) {
    if (localStorage.getItem(STORAGE_KEY) !== fromCookie) {
      localStorage.setItem(STORAGE_KEY, fromCookie);
      localStorage.setItem(EXPIRY_KEY, String(Date.now() + TTL_MS));
    }
    return fromCookie;
  }
  const token = localStorage.getItem(STORAGE_KEY);
  const expiry = localStorage.getItem(EXPIRY_KEY);
  if (!token || !expiry) return null;
  if (Date.now() > Number(expiry)) {
    clearToken();
    return null;
  }
  return token;
}

export function storeToken(token: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, token);
  localStorage.setItem(EXPIRY_KEY, String(Date.now() + TTL_MS));
  writeCookie(COOKIE_NAME, token);
}

export function clearToken(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  deleteCookie(COOKIE_NAME);
}

export function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
